import { Bar, BarChart, CartesianGrid, Cell, Tooltip, XAxis, YAxis } from "recharts";
import { formatClimberCash, formatClimberDelta, formatClimberPct } from "@/components/cost_index/climberFormat";
import { currencySymbol, money } from "@/components/cost_index/format";
import { CARRIED_FILL_OPACITY, monthShort } from "@/components/cost_index/palette";
import { ChartFrame } from "@/components/data";
import { Button } from "@/components/ui/button";
import type { Entity, FulfilmentOtherCosts, FulfilmentOtherCostsPoint } from "../api";

const FILL = "var(--color-ks-purple-4)";
/** The companies as the team names them (C6 books: LTD GBP, OÜ EUR, LLC AED). */
const COMPANY: Record<Entity, string> = { LTD: "LTD", OU: "OÜ", LLC: "LLC" };

export const OTHER_COSTS_HEADING = "Other costs (allocated), outside the index";
export const OTHER_COSTS_CHART_TITLE = "Other costs per unit, by month";
export const OTHER_COSTS_NOT_APPLICABLE = "Other costs are allocated per store, not per ship-to country. Choose All countries to see them.";
export const OTHER_COSTS_NONE = "No closed month carries Other costs for this selection yet. They appear when a company closes its month.";
export const OTHER_COSTS_NONE_CLOSED = "Closed months carry no Other costs for this selection.";

/** The month label the tab's scope line uses ("Jul 2026"). */
export const otherMonthName = (value: string) => new Intl.DateTimeFormat("en-GB", { month: "short", year: "numeric", timeZone: "UTC" }).format(new Date(`${value.slice(0, 7)}-01T12:00:00Z`));

const skus = (count: number) => `${count} channel SKU${count === 1 ? "" : "s"}`;
const companies = (list: Entity[]) => {
  const names = list.map((company) => COMPANY[company]);
  return names.length > 1 ? `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}` : names[0] ?? "";
};
const openTag = (point: FulfilmentOtherCostsPoint) => ` (${companies(point.open_companies)} open)`;

/** One line of engine figures; nothing is computed here (I7). The change appears only when the engine sends one; a
 * partly closed latest month names the companies behind its figure. */
export function otherCostsSummary(other: FulfilmentOtherCosts, baseMonth: string): string {
  if (!other.first_month || !other.latest_month) return "";
  const latest = other.series.find((point) => point.month === other.latest_month);
  const mark = latest?.status === "partial" ? ` (${companies(latest.closed_companies)} closed, ${companies(latest.open_companies)} open)` : "";
  const pieces = [`Latest allocated month, ${otherMonthName(other.latest_month)}${mark}: ${formatClimberCash(other.latest_unit_cost, other.currency, 2)} per unit`];
  if (other.delta_abs !== null) {
    const pct = other.delta_pct === null ? "" : ` (${formatClimberPct(other.delta_pct)})`;
    pieces.push(`since ${otherMonthName(other.first_month)}, same ${skus(other.delta_sku_count)}: ${formatClimberDelta(other.delta_abs, other.currency, 2)}${pct}`);
  }
  pieces.push(`${skus(other.sku_count)} allocated`);
  if (other.fx_basis) pieces.push(`${other.currency} at ${otherMonthName(baseMonth)} rates`);
  return pieces.join(" · ");
}

/** Runs of neighbouring months that pass `keep` and share a tag, as "Jan 2026" or "Jul 2025 to Apr 2026" plus the tag.
 * The engine sends one entry per month, so neighbours in the series are neighbouring months. */
function runs(series: FulfilmentOtherCostsPoint[], keep: (point: FulfilmentOtherCostsPoint) => boolean, tag: (point: FulfilmentOtherCostsPoint) => string = () => ""): string[] {
  const out: string[] = [];
  let start = -1;
  series.forEach((point, index) => {
    if (!keep(point)) return;
    if (start < 0) start = index;
    const next = series[index + 1];
    if (next && keep(next) && tag(next) === tag(point)) return;
    const first = otherMonthName(series[start].month);
    out.push(`${start === index ? first : `${first} to ${otherMonthName(point.month)}`}${tag(point)}`);
    start = -1;
  });
  return out;
}

export function otherCostsMonthsNote(other: FulfilmentOtherCosts): string | null {
  const notLoaded = runs(other.series, (point) => point.status === "not_loaded");
  const open = runs(other.series, (point) => point.status === "open");
  const partial = runs(other.series, (point) => point.status === "partial" && point.unit_cost !== null, openTag);
  const partialEmpty = runs(other.series, (point) => point.status === "partial" && point.unit_cost === null, openTag);
  const empty = runs(other.series, (point) => point.status === "closed" && point.unit_cost === null);
  const parts: string[] = [];
  if (notLoaded.length) parts.push(`No Other costs loaded: ${notLoaded.join(", ")}.`);
  if (open.length) parts.push(`Not yet closed: ${open.join(", ")}.`);
  if (partial.length) parts.push(`Partly closed, drawn lighter: ${partial.join(", ")}.`);
  if (partialEmpty.length) parts.push(`Partly closed, no allocation yet: ${partialEmpty.join(", ")}.`);
  if (empty.length) parts.push(`Closed, no allocation: ${empty.join(", ")}.`);
  return parts.length ? parts.join(" ") : null;
}

/** With a Ship-to pick the store keys are left out; said on `ok` and `none`, so neither reads as the whole scope. */
export function otherCostsExcludedNote(other: FulfilmentOtherCosts): string | null {
  const count = other.excluded_store_keys;
  return (other.status === "ok" || other.status === "none") && count > 0 ? `Other costs are allocated per store, not per ship-to country, so ${count} store channel SKU${count === 1 ? " is" : "s are"} left out of this line.` : null;
}

export function otherCostsNoneText(other: FulfilmentOtherCosts): string {
  return other.series.every((point) => point.status === "open" || point.status === "not_loaded") ? OTHER_COSTS_NONE : OTHER_COSTS_NONE_CLOSED;
}

/** Bar rows for drawing only, as MoneyChart does: a month with no figure has no bar, a partly closed month is drawn lighter. */
export function otherCostsRows(other: FulfilmentOtherCosts) {
  return other.series.map((point) => ({ month: point.month, status: point.status, value: point.unit_cost === null ? null : Number(point.unit_cost), opacity: point.status === "partial" ? CARRIED_FILL_OPACITY : undefined }));
}

export function OtherCostsLine({ other, baseMonth, onExport }: { other: FulfilmentOtherCosts | undefined; baseMonth: string; onExport: () => void }) {
  if (!other || other.status === "unavailable") return null;
  const ok = other.status === "ok";
  const monthsNote = ok ? otherCostsMonthsNote(other) : null;
  const excludedNote = otherCostsExcludedNote(other);
  const rows = otherCostsRows(other);
  return <section aria-label="Other costs (allocated)" data-testid="other-costs-line" className="space-y-2">
    <div className="flex flex-wrap items-baseline justify-between gap-2"><h2 className="text-base font-semibold text-ks-ink">{OTHER_COSTS_HEADING}</h2>{ok && <Button data-nonmutating variant="link" className="px-0 text-sm font-semibold" onClick={onExport}>Other costs CSV</Button>}</div>
    <p className="text-xs text-muted-foreground">{other.note}</p>
    {other.status === "not_applicable" && <p role="note" className="text-sm text-muted-foreground">{OTHER_COSTS_NOT_APPLICABLE}</p>}
    {other.status === "none" && <p role="note" className="text-sm text-muted-foreground">{otherCostsNoneText(other)}</p>}
    {ok && <p data-testid="other-costs-summary" className="text-sm text-ks-ink">{otherCostsSummary(other, baseMonth)}</p>}
    {ok && <ChartFrame title={`${OTHER_COSTS_CHART_TITLE} · ${currencySymbol(other.currency)} per unit`} titleVisible height={220} pointCount={rows.length}>
      <BarChart data={rows} margin={{ top: 12, right: 8, bottom: 4, left: 4 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="var(--color-ks-grey-4)" vertical={false} />
        <XAxis dataKey="month" stroke="var(--color-ks-grey-1)" tickFormatter={monthShort} minTickGap={40} />
        <YAxis stroke="var(--color-ks-grey-1)" width={56} tickFormatter={(value) => money(Number(value), other.currency, 2)} />
        <Tooltip formatter={(value) => money(Number(value), other.currency, 4)} labelFormatter={(label) => monthShort(String(label))} />
        <Bar dataKey="value" name={other.label} fill={FILL} isAnimationActive={false}>
          {rows.map((row) => <Cell key={row.month} data-month={row.month} data-status={row.status} fill={FILL} fillOpacity={row.opacity} />)}
        </Bar>
      </BarChart>
    </ChartFrame>}
    {monthsNote && <p role="note" className="text-xs text-muted-foreground">{monthsNote}</p>}
    {excludedNote && <p role="note" className="text-xs text-muted-foreground">{excludedNote}</p>}
  </section>;
}
